import Button from "components/Button/Button";
import Card from "components/Card/Card";
import { useState } from "react";
import type { MSADecisionReviewPayload } from "types/DataTypes";

import KaboodleModal from "./KaboodleModal";
import OCRMSADecisionModal from "./OCRMSADecisionModal";
import OCRMSADevelopmentModal from "./OCRMSADevelopmentModal";

type LetterType = "decision" | "development";

type OCRLetterModalProps = {
  isOpen: boolean;
  onClose: () => void;
  msaId: string;
  decisionId: number | null;
  // Whether each flow's own waffle switch is on. The sidebar OR's the two
  // switches to decide whether to show this modal at all, so a letter type can
  // be offered here with a disabled switch; gating the buttons on
  // both keeps that combination from reaching a 400 on submit.
  decisionOCREnabled: boolean;
  developmentOCREnabled: boolean;
  initialDecisionReviewPayload?: MSADecisionReviewPayload | null;
};

const OCRLetterModal = ({
  isOpen,
  onClose,
  msaId,
  decisionId,
  decisionOCREnabled,
  developmentOCREnabled,
  initialDecisionReviewPayload,
}: OCRLetterModalProps) => {
  // decisionId is frozen at mount rather than read live: a submit success
  // invalidates the MSA query, which can null it out here on the very next
  // render. Re-deriving eligibility from that would yank the id out from
  // under an already-open OCRMSADecisionModal and collapse this into an
  // empty chooser instead of leaving the open flow alone. Closing and
  // reopening starts a fresh instance with fresh values. Development has no
  // such id to freeze, since completing OCR is what creates its record.
  const [initial] = useState({
    decisionId,
    canProcessDecision: Boolean(decisionId) && decisionOCREnabled,
  });
  const { canProcessDecision } = initial;
  const canProcessDevelopment = developmentOCREnabled;

  const [letterType, setLetterType] = useState<LetterType | null>(null);

  const handleClose = () => {
    setLetterType(null);
    onClose();
  };

  const effectiveLetterType =
    letterType ??
    (canProcessDecision && !canProcessDevelopment
      ? "decision"
      : canProcessDevelopment && !canProcessDecision
        ? "development"
        : null);

  if (
    effectiveLetterType === "decision" &&
    initial.decisionId &&
    canProcessDecision
  ) {
    return (
      <OCRMSADecisionModal
        isOpen={isOpen}
        onClose={handleClose}
        msaId={msaId}
        decisionId={initial.decisionId}
        initialReviewPayload={initialDecisionReviewPayload}
      />
    );
  }

  if (effectiveLetterType === "development" && canProcessDevelopment) {
    return (
      <OCRMSADevelopmentModal
        isOpen={isOpen}
        onClose={handleClose}
        msaId={msaId}
      />
    );
  }

  return (
    <KaboodleModal isOpen={isOpen} onClose={handleClose}>
      <Card heading="Process CMS Letter">
        <div className="flex jc-c">
          {canProcessDecision && (
            <Button onClick={() => setLetterType("decision")}>
              Decision Letter
            </Button>
          )}
          {canProcessDevelopment && (
            <Button onClick={() => setLetterType("development")}>
              Development Letter
            </Button>
          )}
        </div>
      </Card>
    </KaboodleModal>
  );
};

export default OCRLetterModal;
